import { Transaction } from '@/lib/types';
import { formatAUD, stripBasiqPrefix } from '@/lib/format';

export interface DetectedSubscription {
  description: string;
  amount: number;
  frequency: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';
  estimatedAnnualCost: number;
  lastCharge: string;
  occurrences: number;
}

const FREQUENCY_MULTIPLIERS: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

/**
 * Detect recurring charges from transaction history by grouping
 * expenses with the same description and looking for regular intervals.
 */
export function detectSubscriptions(transactions: Transaction[]): DetectedSubscription[] {
  const expenses = transactions
    .filter(t => t.type === 'expense')
    .sort((a, b) => a.date.localeCompare(b.date));

  // Group by normalised description
  const groups = new Map<string, { amount: number; dates: string[] }>();

  for (const t of expenses) {
    const key = stripBasiqPrefix(t.description).trim().toLowerCase();
    if (!key) continue;
    const existing = groups.get(key);
    if (existing) {
      existing.dates.push(t.date);
      // Use the most recent amount
      existing.amount = t.amount;
    } else {
      groups.set(key, { amount: t.amount, dates: [t.date] });
    }
  }

  const results: DetectedSubscription[] = [];

  for (const [desc, { amount, dates }] of groups) {
    if (dates.length < 2) continue;

    // Calculate average gap in days between charges
    const sortedDates = [...dates].sort();
    const gaps: number[] = [];
    for (let i = 1; i < sortedDates.length; i++) {
      const d1 = new Date(sortedDates[i - 1]);
      const d2 = new Date(sortedDates[i]);
      gaps.push((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
    }

    const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;

    // Classify frequency based on average gap
    let frequency: DetectedSubscription['frequency'];
    if (avgGap <= 10) frequency = 'weekly';
    else if (avgGap <= 18) frequency = 'fortnightly';
    else if (avgGap <= 45) frequency = 'monthly';
    else if (avgGap <= 120) frequency = 'quarterly';
    else if (avgGap <= 400) frequency = 'yearly';
    else continue; // Too irregular

    // Check consistency — standard deviation shouldn't be too high
    const variance = gaps.reduce((s, g) => s + Math.pow(g - avgGap, 2), 0) / gaps.length;
    const stdDev = Math.sqrt(variance);
    if (stdDev > avgGap * 0.4) continue; // Too irregular

    const multiplier = FREQUENCY_MULTIPLIERS[frequency];

    // Use original case from first transaction
    const originalDesc = (() => {
      const found = expenses.find(
        t => stripBasiqPrefix(t.description).trim().toLowerCase() === desc
      );
      return found ? stripBasiqPrefix(found.description) : desc;
    })();

    results.push({
      description: originalDesc,
      amount,
      frequency,
      estimatedAnnualCost: amount * multiplier,
      lastCharge: sortedDates[sortedDates.length - 1],
      occurrences: dates.length,
    });
  }

  return results.sort((a, b) => b.estimatedAnnualCost - a.estimatedAnnualCost);
}

export function totalAnnualSubscriptionCost(subs: DetectedSubscription[]): number {
  return subs.reduce((s, sub) => s + sub.estimatedAnnualCost, 0);
}
